
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import DatePicker from "components/cck/DatePicker";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { duplicateMultipleContractsSchema, DuplicateMultipleContractsSchemaInput, DuplicateMultipleContractsSchemaOutput } from "view/contractsNew/utils/contract.schema";

import { DuplicateMultipleContractsDocument } from "__generated__/view/contractsNew/graphql/contract.mutations.generated";

type DuplicateMultipleContractsDialogProps = {
	open: boolean,
	onClose: () => void,
	contracts: {
		id: number;
		validUntil?: number | null;
	}[]
	refetch: () => void
};

const DuplicateMultipleContractsDialog = ({
	open,
	onClose,
	contracts,
	refetch,
}: DuplicateMultipleContractsDialogProps) => {
	const defaultValues = {
		validFrom: null,
		validUntil: null,
	};

	// It is not possible to create a duplicate that starts before the original ends
	const maxValidUntilInContracts = Math.max(...contracts.map(c => c.validUntil).filter(c => typeof c === "number"));
	const minValidUntil = moment(maxValidUntilInContracts).add(1, "day").toDate();

	const { control, handleSubmit, reset, trigger } = useForm<DuplicateMultipleContractsSchemaInput, unknown, DuplicateMultipleContractsSchemaOutput>({
		resolver: zodResolver(duplicateMultipleContractsSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const [ duplicateMultipleContracts, { loading } ] = useMutation(DuplicateMultipleContractsDocument);

	const save = (data: DuplicateMultipleContractsSchemaOutput) => {
		duplicateMultipleContracts({
			variables: {
				contractIds: contracts.map(contract => contract.id),
				validFrom: data.validFrom.valueOf(),
				validUntil: data.validUntil.valueOf(),
			},
		}).then(() => {
			handleOnClose();
			showSnackbar(intl.formatMessage({ id: "DUPLICATE_CONTRACTS_SUCCESS" }), { variant: "success" });
			refetch();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "DUPLICATE_CONTRACTS" })}
			form="duplicateMultipleContractsForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="duplicateMultipleContractsForm">
				<FormGroup
					label={intl.formatMessage({ id: "VALID_FROM" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="validFrom"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker
									{...field}
									minDate={minValidUntil}
									onChange={(date) => field.onChange(date ? new Date(date).setHours(0, 0, 0, 0) : null)}
								/>
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "VALID_UNTIL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="validUntil"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker
									{...field}
									minDate={minValidUntil}
									onChange={(date) => {
										field.onChange(date ? new Date(date).setHours(23, 59, 59, 0) : null);
										trigger("validFrom");
									}}
								/>
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default DuplicateMultipleContractsDialog;
